/**
 * Agent Run API —— 长程运行的 REST 端点（切片 2 波次 B 冻结契约）。
 *
 *   GET    /agent-runs                run 列表（scope=mine/all，meta 分页四元组）
 *   GET    /agent-runs/{id}           run 详情（事件流的权威快照）
 *   POST   /agent-runs/{id}/input     应答 awaiting_input 并续跑
 *   POST   /agent-runs/{id}/cancel    请求取消（当前步骤完成后停止）
 *   POST   /agent-runs                创建即跑（201）
 *   GET    /agent-runs/{id}/stream    SSE 事件流（连接管理见 hooks/useEventStream）
 *
 * 复用 ./index 的 request()（超时 / Bearer / 401 统一登出 / 错误信封）。
 */

import { request } from "./index";
import type { PaginatedResponse } from "../types";
import type {
  AgentRun,
  AgentRunCreatePayload,
  AgentRunInputPayload,
  AgentRunListItem,
  AgentRunStatus,
} from "../types/agentRun";

export const agentRunsApi = {
  /**
   * run 列表（AgentRunListEnvelope）。``scope`` 默认 mine（created_by 为当前
   * 用户）；all 需租户管理员。meta 为 page/pageSize/total/totalPages 四元组。
   */
  list: (params?: {
    status?: AgentRunStatus;
    matterId?: string;
    scheduledTaskId?: string;
    scope?: "mine" | "all";
    page?: number;
    pageSize?: number;
  }) => {
    const qs = new URLSearchParams();
    if (params?.status) qs.set("status", params.status);
    if (params?.matterId) qs.set("matterId", params.matterId);
    if (params?.scheduledTaskId) qs.set("scheduledTaskId", params.scheduledTaskId);
    qs.set("scope", params?.scope ?? "mine");
    if (params?.page) qs.set("page", String(params.page));
    if (params?.pageSize) qs.set("pageSize", String(params.pageSize));
    return request<PaginatedResponse<AgentRunListItem>>(`/agent-runs?${qs}`);
  },

  /** run 详情快照：status / goal / steps / inputs / 预算用量 / 终态与应答。 */
  get: (id: string) => request<{ success: boolean; data: AgentRun }>(`/agent-runs/${id}`),

  /** 应答当前的 awaiting_input 问题；应答后 run 继续执行。 */
  submitInput: (id: string, payload: AgentRunInputPayload) =>
    request<{ success: boolean; data: AgentRun }>(`/agent-runs/${id}/input`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  /** 请求取消。服务端在当前步骤完成后落终态（cancelled）；幂等。 */
  cancel: (id: string) =>
    request<{ success: boolean; data: AgentRun }>(`/agent-runs/${id}/cancel`, {
      method: "POST",
      body: JSON.stringify({}),
    }),

  /** 创建即跑（201）。返回的 run 至少含 id，调用方跳转详情页跟随进度。 */
  create: (payload: AgentRunCreatePayload) =>
    request<{ success: boolean; data: AgentRun }>("/agent-runs", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
};

/**
 * 事件流连接地址。`after` 为客户端已收到的最大 seq（协议 §四）；首次连接
 * （after=0）省略参数，服务端回放全部保留窗口内事件。
 */
export function agentRunStreamUrl(id: string, after: number): string {
  return `/api/v1/jerry/agent-runs/${id}/stream${after > 0 ? `?after=${after}` : ""}`;
}
